import React, { useState } from "react";
import { ArrowRight, CheckCircle2, Layers, Filter, X, ChevronLeft, ChevronRight, Star, Check } from "lucide-react";import { Link } from "react-router-dom";
import { catalog, categories, coverOf, setCoverOverride, getCoverOverride } from "../catalog";
import Reveal from "../components/Reveal";

export default function Products() {
  const [f, setF] = useState("all");
  const [active, setActive] = useState(null); // product for modal
  const [idx, setIdx] = useState(0);
  const [, setTick] = useState(0); // force re-render when cover override changes

  const list = f === "all" ? catalog : catalog.filter((p) => p.category === f);

  // reorder images to put cover first in gallery (based on override or coverIdx)
  const galleryFor = (p) => {
    if (!p) return [];
    const override = getCoverOverride(p.slug);
    const ci = Math.min(
      override !== null ? override : p.coverIdx ?? 0,
      p.images.length - 1
    );
    if (ci === 0) return p.images;
    return [p.images[ci], ...p.images.filter((_, i) => i !== ci)];
  };
  const gallery = active ? galleryFor(active) : [];

  const openProduct = (p) => {
    setActive(p);
    setIdx(0);
  };
  const closeProduct = () => setActive(null);
  const nextImg = () => setIdx((i) => (i + 1) % gallery.length);
  const prevImg = () => setIdx((i) => (i - 1 + gallery.length) % gallery.length);

  // Postavi trenutno prikazanu sliku (gallery[idx]) kao naslovnu za aktivni proizvod
  const markAsCover = () => {
    if (!active) return;
    const currentUrl = gallery[idx];
    const originalIdx = active.images.indexOf(currentUrl);
    if (originalIdx === -1) return;
    setCoverOverride(active.slug, originalIdx);
    setTick((t) => t + 1);
  };

  const isCurrentCover = () => {
    if (!active) return false;
    return gallery[idx] === coverOf(active);
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-neutral-950 text-white overflow-hidden noise">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="absolute -top-32 right-0 w-[520px] h-[520px] rounded-full bg-amber-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-widest text-amber-300">
              <Layers className="w-3.5 h-3.5" /> Katalog proizvoda
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-display mt-5 text-5xl md:text-7xl font-bold leading-tight max-w-3xl">
              Iz vlastite ponude <span className="text-amber-400">izdvajamo</span>.
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-5 text-lg text-neutral-300 max-w-2xl">
              KPMO i KPO ormari, gradilišni i samostojeći ormari, ormarići prve pomoći, antenski, poštanski, telefonski i KMS ormarići – kompletan program za Vaš objekat.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FILTERS */}
      <section className="sticky top-16 md:top-20 z-40 bg-white/95 backdrop-blur border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center gap-3 overflow-x-auto">
          <div className="inline-flex items-center gap-2 text-neutral-500 text-sm shrink-0">
            <Filter className="w-4 h-4" /> Filter:
          </div>
          {categories.map((x) => (
            <button
              key={x.key}
              onClick={() => setF(x.key)}
              className={`btn-primary shrink-0 px-4 py-2 rounded-full text-sm font-medium border ${
                f === x.key
                  ? "bg-neutral-950 text-white border-neutral-950"
                  : "bg-white text-neutral-800 border-neutral-200 hover:border-amber-400 hover:text-amber-600"
              }`}
            >
              {x.label}
            </button>
          ))}
          <div className="ml-auto text-xs text-neutral-500 shrink-0">
            {list.length} proizvod{list.length === 1 ? "" : "a"}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="py-16 md:py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {list.map((p, i) => (
            <Reveal key={p.slug} delay={i * 30}>
              <button
                onClick={() => openProduct(p)}
                className="group card-hover w-full text-left bg-white rounded-3xl overflow-hidden border border-neutral-200 hover:border-amber-400 hover:shadow-xl"
              >
                <div className="aspect-[4/5] overflow-hidden bg-neutral-950 relative">
                  <img
                    src={coverOf(p)}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 bg-amber-400 text-neutral-950 text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-widest">
                    {p.short}
                  </div>
                  {p.images.length > 1 && (
                    <div className="absolute bottom-3 right-3 bg-white/90 text-neutral-900 text-[10px] font-medium px-2 py-1 rounded-full">
                      {p.images.length} slika
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-semibold group-hover:text-amber-600 transition-colors">
                    {p.name}
                  </h3>
                  <p className="mt-2 text-sm text-neutral-600 line-clamp-3">{p.description}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-amber-400">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-6 items-center">
          <h3 className="font-display text-3xl md:text-4xl font-bold text-neutral-950">
            Interesuje Vas nešto posebno? Kontaktirajte nas.
          </h3>
          <div className="md:justify-self-end">
            <Link
              to="/kontakt"
              className="btn-primary inline-flex items-center gap-2 bg-neutral-950 hover:bg-white hover:text-neutral-950 text-white px-6 py-3.5 rounded-full font-medium"
            >
              KONTAKT <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PRODUCT MODAL */}
      {active && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8 bg-neutral-950/80 backdrop-blur-sm" onClick={closeProduct}>
          <div className="relative bg-white rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-hidden grid md:grid-cols-2" onClick={(e) => e.stopPropagation()}>
            {/* Gallery */}
            <div className="relative bg-neutral-950 aspect-square md:aspect-auto md:h-[600px] flex items-center justify-center">
              <img
                src={gallery[idx]}
                alt={active.name}
                className="max-w-full max-h-full object-contain p-8"
              />
              {gallery.length > 1 && (
                <>
                  <button
                    onClick={prevImg}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-amber-400 text-neutral-950 flex items-center justify-center transition-colors"
                    aria-label="Prethodna"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextImg}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-amber-400 text-neutral-950 flex items-center justify-center transition-colors"
                    aria-label="Sljedeća"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
              {/* Thumbnails */}
              {gallery.length > 1 && (
                <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2 px-3 flex-wrap">
                  {gallery.map((im, i) => {
                    const isCover = im === coverOf(active);
                    return (
                      <button
                        key={i}
                        onClick={() => setIdx(i)}
                        title={isCover ? "Trenutna naslovna slika" : ""}
                        className={`relative w-10 h-10 rounded-lg overflow-hidden border-2 ${
                          i === idx ? "border-amber-400" : "border-white/30"
                        }`}
                      >
                        <img src={im} alt="" className="w-full h-full object-cover" />
                        {isCover && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-400 border border-white flex items-center justify-center">
                            <Star className="w-2.5 h-2.5 text-neutral-950 fill-neutral-950" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Info */}
            <div className="p-8 md:p-10 overflow-y-auto">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-amber-500 font-semibold">
                    {active.short}
                  </div>
                  <h2 className="font-display mt-1 text-3xl md:text-4xl font-bold leading-tight text-neutral-950">
                    {active.name}
                  </h2>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {active.images.length > 1 && (
                    <button
                      onClick={markAsCover}
                      disabled={isCurrentCover()}
                      title="Postavi trenutnu sliku kao naslovnu"
                      className={`btn-primary hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border transition-colors ${
                        isCurrentCover()
                          ? "bg-amber-100 text-amber-800 border-amber-300 cursor-default"
                          : "bg-white text-neutral-900 border-neutral-200 hover:bg-amber-400 hover:border-amber-400"
                      }`}
                    >
                      {isCurrentCover() ? <Check className="w-3.5 h-3.5" /> : <Star className="w-3.5 h-3.5" />}
                      {isCurrentCover() ? "Naslovna" : "Postavi kao naslovnu"}
                    </button>
                  )}
                  <button
                    onClick={closeProduct}
                    className="w-10 h-10 rounded-full bg-neutral-100 hover:bg-neutral-950 hover:text-white text-neutral-900 flex items-center justify-center transition-colors"
                    aria-label="Zatvori"
                  >
                  <X className="w-5 h-5" />
                </button>
                </div>
              </div>
              <p className="mt-4 text-neutral-700 leading-relaxed whitespace-pre-line">{active.description}</p>

              <Link
                to="/kontakt"
                onClick={closeProduct}
                className="btn-primary mt-8 inline-flex items-center gap-2 bg-neutral-950 hover:bg-amber-400 hover:text-neutral-950 text-white px-6 py-3 rounded-full text-sm font-medium"
              >
                Zatraži ponudu <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
